import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useBreakpoint, useStuck } from '../../hooks';
import type { ViewMode } from '../../molecules/CdViewSwitcher';
import { cx } from '../../utils';
import styles from './CdIndexPageTemplate.module.scss';

export interface CdIndexPageTemplateProps {
  /** Page name – rendered as a visually hidden <h1> (the header bar shows it visually). */
  title: string;
  view: ViewMode;
  breadcrumbs?: ReactNode;
  /** Page CTAs on the breadcrumb row, before `viewSwitcher`. */
  headerActions?: ReactNode;
  viewSwitcher?: ReactNode;
  /** Tab strip that switches between related lists; rendered under the breadcrumbs. */
  tabs?: ReactNode;
  /** Search field + Filters button row. */
  toolbar: ReactNode;
  appliedFilters?: ReactNode;
  resultMeta?: ReactNode;
  /** Table (≥ md) or record cards (< md) – chosen by the page. */
  list: ReactNode;
  /** Map content for `split` / `map` views. */
  map?: ReactNode;
}

const HeaderEnd = ({
  headerActions,
  viewSwitcher,
  floating = false,
}: {
  headerActions?: ReactNode;
  viewSwitcher?: ReactNode;
  floating?: boolean;
}) => {
  if (!headerActions && !viewSwitcher) return null;
  return (
    <div className={cx(styles.headerEnd, floating && styles.floatingEnd)}>
      {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
      {viewSwitcher && <div className={styles.switcher}>{viewSwitcher}</div>}
    </div>
  );
};

/**
 * Index (list) page layout from the Figma "Index page" frames:
 * - list:  controls stacked above a full-width table/card list. ≥ md the search/filters row pins
 *          under the app header once scrolled to it, and the CTAs + view switcher join it.
 * - split: controls + list on the left, sticky map on the right (≥ lg)
 * - map:   full-bleed map with the controls floating in a card (≥ md).
 *          Below md the same stack as the list sits above a map that fills the screen.
 */
export const CdIndexPageTemplate = ({
  title,
  view,
  breadcrumbs,
  headerActions,
  viewSwitcher,
  tabs,
  toolbar,
  appliedFilters,
  resultMeta,
  list,
  map,
}: CdIndexPageTemplateProps) => {
  const isMd = useBreakpoint('md');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLDivElement>(null);
  const stuck = useStuck(sentinelRef, isMd && view !== 'map');
  const [pinnedHeight, setPinnedHeight] = useState(0);

  // The table toolbar and column headers pin directly below the bar, whose height changes as
  // applied filter tags wrap.
  useEffect(() => {
    const pinned = pinnedRef.current;
    if (!pinned || !isMd) return;
    const observer = new ResizeObserver(() => setPinnedHeight(pinned.offsetHeight));
    observer.observe(pinned);
    return () => observer.disconnect();
  }, [isMd, view]);

  const controls = (
    <>
      <div className={styles.toolbarSlot}>{toolbar}</div>
      {appliedFilters && <div className={styles.applied}>{appliedFilters}</div>}
    </>
  );

  if (view === 'map' && map) {
    return (
      <div className={cx(styles.page, styles.mapView)}>
        <h1 className={styles.srOnly}>{title}</h1>
        <div className={styles.stickyBar}>
          {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
          <div className={styles.floatingControls}>
            {tabs && <div className={styles.tabs}>{tabs}</div>}
            {controls}
            {resultMeta && <div className={styles.meta}>{resultMeta}</div>}
          </div>
          {viewSwitcher && <div className={styles.switcher}>{viewSwitcher}</div>}
        </div>
        <div className={styles.mapArea}>{map}</div>
      </div>
    );
  }

  const isSplit = view === 'split' && Boolean(map);

  return (
    <div className={cx(styles.page, isSplit && styles.splitView)}>
      <h1 className={styles.srOnly}>{title}</h1>
      <div className={styles.listColumn}>
        <div className={styles.stickyBar}>
          {(breadcrumbs || headerActions) && (
            <div className={cx(styles.topRow, Boolean(headerActions) && styles.topRowWithActions)}>
              {breadcrumbs && <div className={styles.breadcrumbs}>{breadcrumbs}</div>}
              {headerActions && (
                <div className={cx(styles.headerEnd, stuck && styles.parked)} inert={stuck}>
                  <div className={styles.headerActions}>{headerActions}</div>
                </div>
              )}
            </div>
          )}
          {tabs && <div className={styles.tabs}>{tabs}</div>}
          <div ref={sentinelRef} className={styles.sentinel} aria-hidden />
          <div
            ref={pinnedRef}
            className={cx(styles.controls, styles.pinned, stuck && styles.stuck)}
          >
            <div className={styles.toolbarSlot}>{toolbar}</div>
            {stuck && (
              <HeaderEnd
                headerActions={headerActions}
                viewSwitcher={isSplit ? undefined : viewSwitcher}
              />
            )}
            {appliedFilters && <div className={styles.applied}>{appliedFilters}</div>}
          </div>
          {!isSplit && viewSwitcher && (
            <div className={cx(styles.switcher, stuck && styles.parked)} inert={stuck}>
              {viewSwitcher}
            </div>
          )}
        </div>
        <div
          className={styles.results}
          style={
            isMd
              ? ({
                  '--cd-table-sticky-top': `calc(var(--pw-header-height) + ${pinnedHeight}px)`,
                } as CSSProperties)
              : undefined
          }
        >
          {resultMeta && <div className={styles.meta}>{resultMeta}</div>}
          {list}
        </div>
      </div>
      {isSplit && (
        <div className={styles.mapColumn}>
          {map}
          <HeaderEnd viewSwitcher={viewSwitcher} floating />
        </div>
      )}
    </div>
  );
};
